/**
 * Staging — edit locally, confirm once.
 *
 * Answers, tags, snoozes and mode changes never go to GitHub per tap. They are
 * staged here (persisted, offline-safe), shown as "staged" on the card, and one
 * global "Confirm & sync" writes them as a SINGLE commit (Git Data API).
 * The LLM is never involved; a run is a separate, explicitly triggered thing.
 */

export type StagedKind = "answer" | "tags" | "snooze" | "mode";

export interface StagedChange {
  path: string;
  /** null = delete this file on confirm (e.g. area back to Standard mode). */
  content: string | null;
  message: string;
  kind: StagedKind;
  stagedAt: string;
}

const KEY = "psychotp.staged";
let cache: Map<string, StagedChange> | null = null;

function storage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

function load(): Map<string, StagedChange> {
  if (cache) return cache;
  const raw = storage()?.getItem(KEY);
  cache = new Map();
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed)) {
        for (const item of parsed as StagedChange[]) {
          if (
            item &&
            typeof item.path === "string" &&
            (typeof item.content === "string" || item.content === null)
          ) {
            cache.set(item.path, item);
          }
        }
      }
    } catch {
      /* corrupt store: start clean rather than crash */
    }
  }
  return cache;
}

function save(): void {
  storage()?.setItem(KEY, JSON.stringify([...load().values()]));
}

export function stagedChanges(): StagedChange[] {
  return [...load().values()].sort((a, b) => a.stagedAt.localeCompare(b.stagedAt));
}

export function stagedCount(): number {
  return load().size;
}

/** Stage (or replace) the pending change for one file. */
export function stageChange(change: Omit<StagedChange, "stagedAt">): void {
  load().set(change.path, { ...change, stagedAt: new Date().toISOString() });
  save();
}

export function unstageChange(path: string): void {
  if (load().delete(path)) save();
}

export function clearStaged(): void {
  cache = new Map();
  save();
}

/** Commit message for a confirm: `console: 2 answers, 1 snooze, 1 mode`. */
export function confirmMessage(changes: StagedChange[]): string {
  const counts = new Map<StagedKind, number>();
  for (const c of changes) counts.set(c.kind, (counts.get(c.kind) ?? 0) + 1);
  const plural: Record<StagedKind, [string, string]> = {
    answer: ["answer", "answers"],
    tags: ["tag change", "tag changes"],
    snooze: ["snooze", "snoozes"],
    mode: ["mode change", "mode changes"],
  };
  const parts = [...counts.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([kind, n]) => `${n} ${plural[kind][n === 1 ? 0 : 1]}`);
  return `console: ${parts.join(", ")}`;
}
